import { Formik, FormikConfig, FormikProps } from "formik";
import { Trans, useTranslation } from "react-i18next";
import {
  preferencesCardBodyClassName,
  preferencesCardClassName,
  preferencesCardHeaderClassName,
} from "./layout";
import {
  mailingAddressFormKeys,
  MailingAddressFormValues,
  SerializeForm,
  sliceProfileValues,
  type FormValues,
} from "components/ProfileForm/support";
import {
  ProfileFormInput,
  ProfileFormSelect,
  SubmitButton,
} from "components/ProfileForm/ProfileFormInputs";
import * as API from "lib/api/types";
import { Banner, Card, CardTitle, typography } from "@dsaui/components";
import { cn } from "@dsaui/utils";
import routes from "routes";
import { States } from "utils/constants";

interface MailingAddressFormProps {
  profile: FormValues;
  ready: boolean;
  submitProfileUpdate: (payload: API.UserProfilePayload) => Promise<void>;
}

const MailingAddressForm = ({
  profile,
  ready,
  submitProfileUpdate,
}: MailingAddressFormProps) => {
  const { t } = useTranslation();
  const initialValues = sliceProfileValues(mailingAddressFormKeys, profile);

  const submitForm: FormikConfig<MailingAddressFormValues>["onSubmit"] = async (
    values,
    { setSubmitting },
  ): Promise<void> => {
    try {
      await submitProfileUpdate(SerializeForm.valuesToMailingAddress(values));
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik<MailingAddressFormValues>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={submitForm}
      validateOnMount
    >
      {(fp: FormikProps<MailingAddressFormValues>) => (
        <form aria-label="mailing address form" onSubmit={fp.handleSubmit}>
          <Card className={preferencesCardClassName}>
            <div className={preferencesCardHeaderClassName}>
              <CardTitle>{t("forms.preferences.mailingAddress.title")}</CardTitle>
            </div>
            <div className={preferencesCardBodyClassName}>
              <Banner>
                <div className="whitespace-pre-line">
                  <Trans
                    components={{
                      1: (
                        // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects link text
                        <a
                          className={cn(
                            typography.profileLink.className,
                            "wrap-break-word",
                          )}
                          href={routes.external.resources + "faq/"}
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey="forms.preferences.mailingAddress.banner"
                  />
                </div>
              </Banner>

              <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
                <div className="min-w-0 flex-1">
                  <ProfileFormInput name="mailingLine1" fp={fp} />
                </div>
                <div className="min-w-0 flex-1">
                  <ProfileFormInput name="mailingLine2" fp={fp} />
                </div>
              </div>

              <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
                <div className="min-w-0 flex-1">
                  <ProfileFormInput name="mailingCity" fp={fp} />
                </div>
                <div className="min-w-0 flex-1">
                  <ProfileFormSelect name="mailingState" fp={fp} required>
                    {States.map((choice: string) => (
                      <option key={choice} value={choice}>
                        {t(`countries.US.states.${choice}.displayName`)}
                      </option>
                    ))}
                  </ProfileFormSelect>
                </div>
                <div className="min-w-0 flex-1">
                  <ProfileFormInput name="mailingZip" fp={fp} maxLength={10} />
                </div>
              </div>

              <div>
                <SubmitButton
                  block
                  data-testid="save-mailing-address"
                  fp={fp}
                  ready={ready}
                >
                  {t("save")}
                </SubmitButton>
              </div>
            </div>
          </Card>
        </form>
      )}
    </Formik>
  );
};

export default MailingAddressForm;
